import { useState } from 'react';

// Hook compartido para módulos clínicos con múltiples registros por paciente
// (ISAK, GMFCS, Threshold, y futuros). Encapsula el patrón de "modo edición
// por-registro" ya usado por GMFCS/Threshold en FichaKinesio.jsx: cada
// registro tiene su propio estado de acordeón (abierto) y de edición
// (editando), independiente del modo edición global de la ficha.
function useRegistrosConHistorial(registrosIniciales = []) {
  const [records, setRecords]     = useState(registrosIniciales);
  const [abierto, setAbierto]     = useState({});  // { id: bool }
  const [editando, setEditando]   = useState({});  // { id: valor en edición | undefined }
  const [guardando, setGuardando] = useState({});  // { id: bool }
  const [guardado, setGuardado]   = useState({});  // { id: bool }
  const [creando, setCreando]     = useState(false);
  const [confirmarEliminarId, setConfirmarEliminarId] = useState(null);
  const [error, setError] = useState('');
  // { id: bool } — true mientras un registro creado por agregarNuevo nunca
  // pasó por un guardar() (o marcarGuardado()) exitoso. Distingue "cancelar
  // un registro recién creado, nunca guardado" (debe borrarse del backend)
  // de "cancelar una edición de un registro con contenido real" (solo
  // descarta cambios). Se limpia al primer guardado exitoso.
  const [nuncaGuardado, setNuncaGuardado] = useState({});

  const toggleAbierto = (id) => {
    setAbierto(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Crea un registro nuevo vía crearFn (llamada a la API) y lo deja abierto
  // Y en modo edición de entrada — esto es lo que evita que un registro
  // recién creado aparezca en modo solo-lectura.
  const agregarNuevo = async (crearFn, valoresIniciales = {}) => {
    setCreando(true);
    try {
      const nuevo = await crearFn();
      setRecords(prev => [nuevo, ...prev]);
      setAbierto(prev => ({ ...prev, [nuevo.id]: true }));
      setEditando(prev => ({ ...prev, [nuevo.id]: valoresIniciales }));
      setNuncaGuardado(prev => ({ ...prev, [nuevo.id]: true }));
      return nuevo;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setCreando(false);
    }
  };

  const iniciarEdicion = (id, valoresActuales = {}) => {
    setEditando(prev => ({ ...prev, [id]: valoresActuales }));
    setAbierto(prev => ({ ...prev, [id]: true }));
  };

  const actualizarEdicion = (id, cambios) => {
    setEditando(prev => ({ ...prev, [id]: { ...prev[id], ...cambios } }));
  };

  // Marca un registro como "con contenido real guardado", limpiando el flag
  // de nuncaGuardado sin pasar por el flujo completo de guardar(). Necesario
  // para módulos donde el guardado real ocurre fuera del hook (ej. ISAK, que
  // maneja sus propios campos y llama a la API directamente) pero igual
  // quieren que cancelarEdicion respete la distinción de más abajo.
  const marcarGuardado = (id) => {
    setNuncaGuardado(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  // Sale de modo edición y colapsa el acordeón, SIN chequear nuncaGuardado
  // ni tocar el backend bajo ninguna circunstancia. A diferencia de
  // cancelarEdicion, no lee ningún estado capturado por closure — solo
  // aplica actualizadores funcionales (prev => ...) sobre setEditando/
  // setAbierto, que siempre operan contra el estado más reciente sin
  // importar cuándo ni desde qué closure se invoquen. Por eso es la única
  // función segura para cerrar edición desde un callback diferido (ej. un
  // setTimeout programado varios renders antes de que se ejecute) — un
  // cancelarEdicion diferido de esa forma podría leer un nuncaGuardado
  // desactualizado (capturado en el momento en que se creó el closure) y
  // borrar por error un registro que ya se guardó con éxito.
  // Usar para cierres automáticos post-guardado (ISAK, que guarda contra la
  // API por su cuenta y no pasa por guardar() de este hook). El botón
  // "Cancelar" que el usuario clickea a propósito debe seguir usando
  // cancelarEdicion, nunca esta función.
  const cerrarEdicion = (id) => {
    setEditando(prev => { const n = { ...prev }; delete n[id]; return n; });
    setAbierto(prev => ({ ...prev, [id]: false }));
  };

  // Sale de modo edición y colapsa el acordeón del registro. Si el registro
  // nunca tuvo un guardado exitoso (recién creado por agregarNuevo y jamás
  // guardado), en vez de solo descartar cambios lo elimina del backend
  // directamente — sin pedir confirmación, porque el usuario ya expresó la
  // intención al cancelar. Para eso necesita `eliminarFn`. Un registro que sí
  // tuvo contenido guardado alguna vez se comporta igual que antes: cancelar
  // solo descarta los cambios de esta edición, nunca borra nada.
  // Reservada exclusivamente para el botón "Cancelar" clickeado por el
  // usuario — nunca para cierres automáticos diferidos (ver cerrarEdicion).
  const cancelarEdicion = async (id, eliminarFn) => {
    if (nuncaGuardado[id] && eliminarFn) {
      try {
        await eliminarFn();
        setRecords(prev => prev.filter(r => r.id !== id));
      } catch (err) {
        setError(err.message);
      }
      setNuncaGuardado(prev => { const n = { ...prev }; delete n[id]; return n; });
    }
    setEditando(prev => { const n = { ...prev }; delete n[id]; return n; });
    setAbierto(prev => ({ ...prev, [id]: false }));
  };

  // guardarFn recibe el valor actual de editando[id] y debe devolver el
  // registro actualizado (respuesta de la API). Al guardar, muestra
  // "guardado" transitoriamente y luego sale de modo edición Y colapsa el
  // acordeón, igual que gmfcsEditando/threshEditando hoy.
  const guardar = async (id, guardarFn) => {
    const edicion = editando[id];
    if (!edicion) return;
    setGuardando(prev => ({ ...prev, [id]: true }));
    try {
      const actualizado = await guardarFn(edicion);
      setRecords(prev => prev.map(r => (r.id === id ? actualizado : r)));
      setNuncaGuardado(prev => { const n = { ...prev }; delete n[id]; return n; });
      setGuardado(prev => ({ ...prev, [id]: true }));
      setTimeout(() => {
        setGuardado(prev => ({ ...prev, [id]: false }));
        cerrarEdicion(id);
      }, 2000);
      return actualizado;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setGuardando(prev => ({ ...prev, [id]: false }));
    }
  };

  const pedirEliminar = (id) => setConfirmarEliminarId(id);
  const cancelarPedidoEliminar = () => setConfirmarEliminarId(null);

  const eliminar = async (id, eliminarFn) => {
    try {
      await eliminarFn();
      setRecords(prev => prev.filter(r => r.id !== id));
      setEditando(prev => { const n = { ...prev }; delete n[id]; return n; });
      setNuncaGuardado(prev => { const n = { ...prev }; delete n[id]; return n; });
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setConfirmarEliminarId(null);
    }
  };

  return {
    records, setRecords,
    abierto, setAbierto, toggleAbierto,
    editando, actualizarEdicion,
    guardando, guardado,
    creando,
    confirmarEliminarId, pedirEliminar, cancelarPedidoEliminar,
    error, setError,
    agregarNuevo, iniciarEdicion, cancelarEdicion, cerrarEdicion, guardar, eliminar, marcarGuardado,
  };
}

export default useRegistrosConHistorial;
